// Auth/session context. Holds the bearer token (Supabase JWT in prod; a pasted
// dev token locally), the current app user (/iam/me incl. status + orgs), and
// the active org for multi-org scoping. The token is attached to every iamApi
// request via setAuthToken.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import iamApi, { setAuthToken } from '../services/iamApi';
import { setActiveOrg as setActiveOrgHeader,
         setActiveProject as setActiveProjectHeader } from '../services/authToken';
import { supabase, authRedirectError } from '../services/supabaseClient';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(() => localStorage.getItem('rmdv_token') || null);
  const [me, setMe] = useState(null);
  const [activeOrg, setActiveOrg] = useState(() => localStorage.getItem('rmdv_org') || null);
  const [activeProject, setActiveProject] = useState(() => localStorage.getItem('rmdv_project') || null);
  const [loading, setLoading] = useState(true);
  // Soft, non-destructive error from a refresh() that failed WITHOUT proving the
  // session is actually invalid (network blip / 5xx) — see the 401-vs-network
  // split in refresh() below. Null means "no outstanding refresh problem"; a
  // consumer (AuthGate) can render it without us forcing a logout.
  const [refreshError, setRefreshError] = useState(null);
  // #157: a failed magic-link/OAuth redirect (expired or already-used link,
  // denied consent) — captured once at module-load time by supabaseClient's
  // captureAuthRedirectError(). Surfaced by LoginPage; the user (or a later,
  // successful auth) can clear it via clearAuthCallbackError below.
  const [authCallbackError, setAuthCallbackError] = useState(authRedirectError);
  const clearAuthCallbackError = useCallback(() => setAuthCallbackError(null), []);
  // #158: TOTP 2FA sign-in challenge. A Supabase session can be established
  // (token present) at authenticator-assurance-level 1 while the account has a
  // verified TOTP factor, which requires stepping up to aal2 before the app is
  // usable — Supabase's own recommended check is comparing
  // getAuthenticatorAssuranceLevel()'s currentLevel vs nextLevel. Re-checked
  // after every Supabase auth-state change (below) so a fresh sign-in, a token
  // refresh, and a successful `mfa.verify()` (which itself fires an auth-state
  // change) all keep this in sync without a manual reload.
  const [mfaRequired, setMfaRequired] = useState(false);
  const refreshMfaStatus = useCallback(async () => {
    if (!supabase) { setMfaRequired(false); return; }
    try {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error || !data) { setMfaRequired(false); return; }
      setMfaRequired(data.nextLevel === 'aal2' && data.currentLevel !== data.nextLevel);
    } catch {
      // No `.auth.mfa` on the client (e.g. a minimal test double) — treat as
      // "nothing to step up to" rather than throwing.
      setMfaRequired(false);
    }
  }, []);
  // Only the FIRST refresh() should flip the global `loading` flag — that's what
  // AuthGate (App.js) uses to show a full-screen spinner while we don't yet know
  // the sign-in state. A later refresh() (re-syncing `me`/`orgs` after an org is
  // created/renamed/deleted) must NOT retrigger that spinner: doing so unmounts
  // then remounts every authenticated route (AuthGate renders the spinner in
  // place of `children`), wiping any local component state set right after the
  // refresh() call (e.g. a just-shown success message). See AcceptInvite.jsx's
  // own comment on this same hazard.
  const hasLoadedOnce = useRef(false);

  // Keep the shared API-client header in sync with the active org so every core/chat
  // request is scoped to it (SAAS.3.1).
  useEffect(() => {
    setActiveOrgHeader(activeOrg);
  }, [activeOrg]);

  // Keep the shared API-client header in sync with the active project so every core/chat
  // request scopes reads + stamps writes to the selected project (#125). Without this the
  // selected project never reached the backend and writes landed outside any project.
  useEffect(() => {
    setActiveProjectHeader(activeProject);
  }, [activeProject]);

  const setToken = useCallback((t) => {
    if (t) localStorage.setItem('rmdv_token', t);
    else localStorage.removeItem('rmdv_token');
    setAuthToken(t);
    setTokenState(t);
  }, []);

  const refresh = useCallback(async () => {
    if (!token) {
      setMe(null);
      setLoading(false);
      return;
    }
    setAuthToken(token);
    const isFirstLoad = !hasLoadedOnce.current;
    if (isFirstLoad) setLoading(true);
    try {
      const data = await iamApi.me();
      setMe(data);
      setRefreshError(null);
      if (!localStorage.getItem('rmdv_org') && data.orgs && data.orgs.length) {
        setActiveOrg(data.orgs[0].id);
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        // A real auth failure — the token is genuinely no longer valid, so
        // dropping to logged-out is correct.
        setMe(null);
        setRefreshError(null);
      } else {
        // Transient network/5xx error: the token may still be perfectly good.
        // Keep the existing session (don't touch `me`) and surface a soft
        // error instead of silently logging the user out.
        setRefreshError(e?.message || 'Could not refresh your session. Retrying may help.');
      }
    } finally {
      hasLoadedOnce.current = true;
      if (isFirstLoad) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // When Supabase is configured, drive the token from the GoTrue session
  // (initial + on change). Otherwise the dev-token path in LoginPage is used.
  useEffect(() => {
    if (!supabase) return undefined;
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session?.access_token) setToken(data.session.access_token);
    });
    refreshMfaStatus();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setToken(session?.access_token || null);
      // A newly-established session (e.g. clicking a valid magic link right after
      // an earlier, expired one) supersedes any stale redirect error.
      if (session?.access_token) setAuthCallbackError(null);
      // #158: re-check on every auth-state change, including the
      // 'MFA_CHALLENGE_VERIFIED' event Supabase fires after a successful
      // mfa.verify() — that's what flips mfaRequired back off post-challenge.
      refreshMfaStatus();
    });
    return () => sub?.subscription?.unsubscribe();
  }, [setToken, refreshMfaStatus]);

  const chooseProject = useCallback((id) => {
    if (id) localStorage.setItem('rmdv_project', id);
    else localStorage.removeItem('rmdv_project');
    setActiveProject(id || null);
  }, []);

  const chooseOrg = useCallback((id) => {
    if (id) localStorage.setItem('rmdv_org', id);
    else localStorage.removeItem('rmdv_org');
    setActiveOrg(id || null);
    // Active project belongs to an org; clear it when the org changes so we don't
    // carry a stale project across orgs (SelectContent reselects within the new org).
    localStorage.removeItem('rmdv_project');
    setActiveProject(null);
  }, []);

  const logout = useCallback(() => {
    if (supabase) supabase.auth.signOut();
    setToken(null);
    setMe(null);
    setRefreshError(null);
    // Next sign-in is a fresh session — show the loading spinner while it resolves.
    hasLoadedOnce.current = false;
    localStorage.removeItem('rmdv_org');
    setActiveOrg(null);
    localStorage.removeItem('rmdv_project');
    setActiveProject(null);
  }, [setToken]);

  // useMemo: this value reaches every authenticated screen, so a fresh object on each
  // provider render re-renders the whole app (#943,
  // react/jsx-no-constructed-context-values).
  const value = useMemo(() => ({
    token, setToken, me, refresh, loading, refreshError, activeOrg, chooseOrg, logout,
    activeProject, chooseProject, authCallbackError, clearAuthCallbackError,
    mfaRequired, refreshMfaStatus,
    isSuperAdmin: !!(me && me.is_super_admin),
    orgs: (me && me.orgs) || [],
    user: me && me.user,
  }), [token, setToken, me, refresh, loading, refreshError, activeOrg, chooseOrg, logout,
    activeProject, chooseProject, authCallbackError, clearAuthCallbackError,
    mfaRequired, refreshMfaStatus]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
export default AuthCtx;
